import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Shield, Award, Users } from "lucide-react";
import { teamMembers } from "../data/mock";

const About = () => {
  return (
    <section id="about" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* Mission Statement */}
        <div className="max-w-4xl mx-auto mb-16">
          <div className="text-center mb-8">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              About SMX Medical
            </h2>
            <p className="text-xl text-gray-600">
              Your trusted partner in medical excellence
            </p>
          </div>

          <Card className="border-2 border-[#0056c5]/20 shadow-lg">
            <CardContent className="p-8 md:p-12">
              <div className="prose prose-lg max-w-none">
                <p className="text-gray-700 leading-relaxed mb-6">
                  SMX Medical Services is your trusted partner in delivering exceptional medical and first aid coverage 
                  for a wide range of events. From exhilarating marathons and vibrant food festivals to family fun days 
                  and adrenaline-fueled motorsport events, we've got you covered.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  We specialise in providing medical support for spectacular firework displays and more, ensuring the 
                  safety and well-being of everyone at your event.
                </p>
                <div className="bg-[#0056c5]/5 rounded-lg p-6 border-l-4 border-[#0056c5]">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Our Mission</h3>
                  <p className="text-gray-700 leading-relaxed m-0">
                    We strive to deliver comprehensive medical support at events, prioritizing health and safety through 
                    our experienced team backed by reliable resources.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Team Section */}
        <div className="mb-16">
          <h3 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Our Professional Team
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {teamMembers.map((member) => (
              <Card key={member.id} className="border-2 hover:border-[#0056c5] transition-all duration-300 hover:shadow-xl">
                <CardHeader>
                  <div className="w-16 h-16 bg-[#0056c5]/10 rounded-full flex items-center justify-center mb-4">
                    <Users className="h-8 w-8 text-[#0056c5]" />
                  </div>
                  <CardTitle className="text-xl text-gray-900">
                    {member.name}
                  </CardTitle>
                  <p className="text-sm text-[#0056c5] font-semibold">
                    {member.role}
                  </p>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700">{member.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Team Photo */}
          <div className="relative rounded-2xl overflow-hidden shadow-2xl">
            <img
              src="https://assets.zyrosite.com/cdn-cgi/image/format=auto,w=1920,h=800,fit=crop/dJobQe0laqSVpB88/crew1-YBgb98qn6WHN9G7o.png"
              alt="SMX Medical Team"
              className="w-full h-[400px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end">
              <div className="p-8">
                <h4 className="text-2xl font-bold text-white mb-2">
                  Dedicated to Your Safety
                </h4>
                <p className="text-gray-200">
                  Our friendly, professional staff with frontline NHS experience
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* What Sets Us Apart */}
        <div>
          <h3 className="text-3xl font-bold text-gray-900 text-center mb-12">
            What Sets Us Apart
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="bg-white border-2 border-[#0056c5]/20 hover:shadow-xl transition-shadow">
              <CardHeader>
                <Shield className="h-12 w-12 text-[#0056c5] mb-4" />
                <CardTitle className="text-xl text-gray-900">
                  Top-Notch Expertise
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700">
                  Our team includes Ambulance Technicians, HCPC Paramedics, and FREC-certified staff, 
                  all fully trained and experienced in emergency medical care.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white border-2 border-[#0056c5]/20 hover:shadow-xl transition-shadow">
              <CardHeader>
                <Award className="h-12 w-12 text-[#0056c5] mb-4" />
                <CardTitle className="text-xl text-gray-900">
                  Peace of Mind
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700">
                  Every team member undergoes Enhanced DBS checks and regular proficiency assessments, 
                  ensuring you receive the best care possible.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white border-2 border-[#0056c5]/20 hover:shadow-xl transition-shadow">
              <CardHeader>
                <Users className="h-12 w-12 text-[#0056c5] mb-4" />
                <CardTitle className="text-xl text-gray-900">
                  Friendly & Approachable
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700">
                  We believe in building positive relationships with our clients and their guests. 
                  Our staff are always ready to engage, offering a reassuring presence.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Resources Section */}
        <div className="mt-16">
          <Card className="border-2 border-[#0056c5]/20 overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="p-8 md:p-12">
                <h3 className="text-3xl font-bold text-gray-900 mb-6">
                  Our Resources
                </h3>
                <div className="space-y-4">
                  <p className="text-gray-700 leading-relaxed">
                    At SMX Medical, our fleet is comprised of a fully equipped front line ambulance and a capable 
                    off-road vehicle. Both have blue lights for an emergency response.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Each vehicle is outfitted to the highest standards, and all equipment undergoes a rigorous 
                    servicing and audit schedule.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Our self-sufficiency extends to communications, power, heating, and lighting - ensuring that 
                    we can deliver exceptional service in any environment.
                  </p>
                </div>
              </div>
              <div className="relative h-[400px] lg:h-auto">
                <img
                  src="https://assets.zyrosite.com/cdn-cgi/image/format=auto,w=768,h=512,fit=crop/dJobQe0laqSVpB88/2d5a0506-7c66-468b-a77b-d6a0f9c86e82-YleQgRx9VKS5by5b.jpg"
                  alt="SMX Medical Resources"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default About;
